import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shell } from '../components/Shell';
import { useAuth } from '../lib/auth';

const PLAN_DETAILS: Record<string, { name: string; price: string; cadence: string }> = {
  solo: { name: 'Solo', price: '€19.00', cadence: 'per month' },
  agency: { name: 'Agency', price: '€79.00', cadence: 'per month' },
};

export function CheckoutPage() {
  const [params] = useSearchParams();
  const planKey = params.get('plan') || 'solo';
  const plan = PLAN_DETAILS[planKey] || PLAN_DETAILS.solo;
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onPay = (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setTimeout(() => {
      navigate('/checkout/success?plan=' + planKey);
    }, 1400);
  };

  return (
    <Shell centerLabel={`Checkout · ${plan.name}`}>
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-14 py-12 md:py-16">
          <span className="meta">Checkout · payment will run through Paddle once approved</span>
          <h1 className="font-medium mt-2 mb-10" style={{ fontSize: 'clamp(28px, 3.4vw, 40px)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
            Subscribe to <span className="grad-text">{plan.name}</span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            <aside>
              <span className="meta mb-3 block">Order summary</span>
              <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--radius)', padding: 24, background: 'white' }}>
                <div className="flex items-baseline justify-between pb-4 mb-4" style={{ borderBottom: '1px solid var(--rule)' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 17, letterSpacing: '-0.01em' }}>{plan.name}</div>
                    <div className="meta mt-1">Monthly subscription · cancel anytime</div>
                  </div>
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 14 }}>{plan.price}</div>
                </div>
                <div className="flex items-baseline justify-between text-[13px] mb-2" style={{ color: 'var(--muted-ink)' }}>
                  <span>Subtotal</span>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{plan.price}</span>
                </div>
                <div className="flex items-baseline justify-between text-[13px] mb-3" style={{ color: 'var(--muted-ink)' }}>
                  <span>VAT (calculated by Paddle)</span>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>·</span>
                </div>
                <div className="flex items-baseline justify-between pt-3" style={{ borderTop: '1px solid var(--rule)' }}>
                  <span className="font-medium text-[15px]">Due today</span>
                  <span className="font-medium" style={{ fontSize: 20, letterSpacing: '-0.02em' }}>{plan.price}</span>
                </div>
              </div>

              <div className="mt-6 p-5" style={{ background: 'var(--paper-2)', borderRadius: 'var(--radius)' }}>
                <span className="meta mb-2 block">After payment</span>
                <p className="text-[13px] leading-[1.55]" style={{ color: 'rgba(13,11,31,0.78)' }}>
                  Your workspace upgrades immediately. White-label kicks in on your live banner within 60 seconds. You can change billing details from the dashboard.
                </p>
              </div>

              <div className="meta mt-6">
                Need an annual plan? <a href="mailto:hello@wedpo.com?subject=Annual%20plan%20enquiry" className="underline" style={{ color: 'var(--ink)' }}>Talk to us</a> for 2 months free.
              </div>
            </aside>

            <form onSubmit={onPay} className="flex flex-col gap-5">
              <label className="field">
                <span className="field-label">Email</span>
                <input type="email" required defaultValue={user?.email ?? ''} />
                <span className="field-hint">Pre-filled from your account. Receipts and account login use this address.</span>
              </label>

              <label className="field">
                <span className="field-label">Card information</span>
                <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--radius)', background: 'white' }}>
                  <input type="text" placeholder="1234 1234 1234 1234" inputMode="numeric" style={{ border: 0, borderBottom: '1px solid var(--rule)', borderRadius: 0 }} />
                  <div className="grid grid-cols-2">
                    <input type="text" placeholder="MM / YY" style={{ border: 0, borderRight: '1px solid var(--rule)', borderRadius: 0 }} />
                    <input type="text" placeholder="CVC" style={{ border: 0, borderRadius: 0 }} />
                  </div>
                </div>
              </label>

              <label className="field">
                <span className="field-label">Cardholder name</span>
                <input type="text" placeholder="Jane Doe" />
              </label>

              <label className="field">
                <span className="field-label">Billing country</span>
                <select defaultValue="DE">
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="IL">Israel</option>
                  <option value="GB">United Kingdom</option>
                  <option value="US">United States</option>
                  <option value="NL">Netherlands</option>
                  <option value="ES">Spain</option>
                  <option value="IT">Italy</option>
                </select>
              </label>

              <button type="submit" className="btn mt-2" disabled={submitting}>
                <span>{submitting ? 'Processing…' : `Pay ${plan.price} and unlock ${plan.name}`}</span>
              </button>

              <p className="text-[11px] leading-[1.55]" style={{ color: 'var(--muted-ink)' }}>
                Payments handled by Paddle (separate controller). VAT calculated at checkout. By clicking pay you accept the{' '}
                <a href="https://app.wedpo.com/license-agreement" className="underline" style={{ color: 'var(--ink)' }}>License Agreement</a>{' '}
                and{' '}
                <a href="https://wedpo.com/privacy-policy" className="underline" style={{ color: 'var(--ink)' }}>Privacy Policy</a>. See our{' '}
                <a href="https://scanner.wedpo.com/refund-and-cancellation" className="underline" style={{ color: 'var(--ink)' }}>refund and cancellation policy</a>.
              </p>
            </form>
          </div>
        </div>
      </section>
    </Shell>
  );
}

export function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const planKey = params.get('plan') || 'solo';
  const plan = PLAN_DETAILS[planKey] || PLAN_DETAILS.solo;
  return (
    <Shell centerLabel="Welcome to Pro">
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[720px] mx-auto px-6 md:px-14 py-20 text-center">
          <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
          <h1 className="font-medium mb-4" style={{ fontSize: 'clamp(28px, 3.4vw, 40px)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
            You&apos;re on <span className="grad-text">{plan.name}</span>.
          </h1>
          <p className="text-[15px] leading-[1.55] mb-8 max-w-[520px] mx-auto" style={{ color: 'rgba(13,11,31,0.7)' }}>
            White-label is now live on your banner, you can add more domains, and the consent log is unlocked. Receipt will land in your inbox.
          </p>
          <a href="/dashboard" className="btn inline-flex">
            <span>Go to dashboard</span> <span className="slash">/</span>
          </a>
        </div>
      </section>
    </Shell>
  );
}
